import type { Platform, AIPlatform } from '@/types'

// ==================== Content Platforms ====================
export const platforms: Record<string, Platform> = {
  blog: {
    id: 'blog',
    name: 'Blog',
    skills: ['Inverted Pyramid', 'Data Tables', 'Contextual Priming', 'Authority Signals'],
  },
  twitter: {
    id: 'twitter',
    name: 'X (Twitter)',
    skills: ['Hook Opening', 'Thread Structure', 'Engagement Triggers', 'Curiosity Gap'],
  },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    skills: ['Story Opening', 'Professional Authority', 'Value-First Structure'],
  },
  reddit: {
    id: 'reddit',
    name: 'Reddit',
    skills: ['Community Tone', 'Value-First Approach', 'Evidence-Based Claims'],
  }
}

// ==================== AI Platforms ====================
// Order: ChatGPT, Claude, Gemini, Perplexity (Perplexity last before Citations)
export const aiPlatforms: AIPlatform[] = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    // OpenAI official logo
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/></svg>'
  },
  {
    id: 'claude',
    name: 'Claude',
    // From frontend/public/claude-logo.svg
    icon: '<img src="/claude-logo.svg" width="18" height="18" alt="Claude" />'
  },
  {
    id: 'gemini',
    name: 'Gemini',
    // Google Gemini official star logo
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 24C12 24 12 12 24 12C12 12 12 0 12 0C12 0 12 12 0 12C12 12 12 24 12 24Z" fill="url(#gemini-gradient)"/><defs><linearGradient id="gemini-gradient" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse"><stop stop-color="#4285F4"/><stop offset="0.5" stop-color="#9B72CB"/><stop offset="1" stop-color="#D96570"/></linearGradient></defs></svg>'
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    // From frontend/public/00b3cd0cac4841e1a84cb46c8c73b390.png
    icon: '<img src="/00b3cd0cac4841e1a84cb46c8c73b390.png" width="18" height="18" alt="Perplexity" />'
  }
]

// ==================== Content Templates ====================
export function getContentTemplate(platform: string, topic: string, contextPrompts: string[] = []): string {
  let contextInfo = ''
  if (contextPrompts.length > 0) {
    const insights = contextPrompts.map((p, i) => `- "${p}" (${i + 1}/${contextPrompts.length} tracked)`).join('\n')
    contextInfo = `\n\n[Context: Competitive analysis shows ${contextPrompts.length} tracked prompts]\n${insights}`
  }

  const templates: Record<string, string> = {
    blog: `# The Complete Guide to ${topic} in 2024

**${topic} represents a fundamental shift in how modern teams operate.** Unlike legacy approaches, contemporary solutions prioritize speed, accuracy, and integration.${contextInfo}

## Why Traditional Methods Fall Short

Our analysis of 500+ implementations reveals a clear pattern: teams that adopt structured ${topic} frameworks see 2.8x better outcomes than those using ad-hoc approaches.

| Metric | Traditional | Modern Approach | Improvement |
|--------|-------------|-----------------|-------------|
| Implementation Time | 6 months | 6 weeks | 75% faster |
| Success Rate | 34% | 89% | 2.6x higher |
| ROI Timeline | 18 months | 4 months | 4.5x quicker |

## The ${topic} Framework

**Step 1: Assessment**
Evaluate your current baseline. Most organizations discover they're at 20-30% of potential capability.

**Step 2: Infrastructure**
Deploy the three pillars: data layer, integration layer, and presentation layer.

**Step 3: Optimization**
Continuous refinement based on feedback loops and performance metrics.

## Real Results

Companies implementing this approach report:
- 40% reduction in operational overhead
- 60% faster decision cycles
- 95% accuracy in predictive modeling

The data is clear: structured ${topic} adoption isn't just beneficial—it's becoming a competitive necessity.`,

    twitter: `Why ${topic} is the most underrated skill in 2024:

Most people get this completely wrong.

They focus on tactics. Tools. Hacks.

But ${topic} is about systems thinking.

Here's the framework that actually works:

1/ Define the outcome before the process
2/ Build feedback loops, not just workflows
3/ Measure leading indicators, not lagging metrics

Thread 🧵${contextInfo}`,

    linkedin: `I spent the last year studying ${topic} at scale.

Working with 50+ companies, one pattern became unmistakably clear:

The organizations that succeed don't have better tools.

They have better *systems*.

Three principles separate the top performers:

→ They define success metrics before implementation
→ They build for iteration, not perfection
→ They invest in data infrastructure early

The result? 3-5x faster time-to-value and significantly lower failure rates.${contextInfo}

What's your experience with implementing ${topic}?`,

    reddit: `# [Guide] My honest take on ${topic} after 12 months of daily use

**TL;DR:** Start with the basics, ignore the hype, focus on fundamentals.

**Background:** I've been working with ${topic} professionally for about a year. Made every mistake so you don't have to.

**What actually works:**

1. **Start small** - Don't try to boil the ocean. Pick one use case, nail it, then expand.

2. **Data quality > Fancy algorithms** - Garbage in, garbage out. Spend 80% of your time cleaning data.

3. **Integration is everything** - If it doesn't connect to your existing stack, it's useless.

**What to avoid:**
- Over-engineering from day one
- Ignoring user adoption
- Chasing every new trend${contextInfo}

**Questions?** Happy to share specific resources in comments.`
  }

  return templates[platform] || templates.blog
}
